import { useNavigate } from "react-router-dom";
import DataTable, { Column } from "../../components/DataTable";
import Tag from "../../components/Tags";
import { copyIcon, edit, link, playIcon, trash } from "../../asset";
import {
  formatDateTime,
  popupNavigation,
} from "../../util/functions/UniversalFunctions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useEffect, useState } from "react";
import {
  fetchForms,
  setFormFilters,
  setPage,
  setSize,
} from "../../features/forms/FormSlice";
import { TooltipWrapper } from "../../components";
import { FormItem } from "../../types/formType";
import PaginationBar from "../../components/PaginationBar";
import useConfirmToast from "../../util/hooks/useConfirmToast";
import FormApis from "../../api/formApis";
import { toast } from "sonner";
import ShareLinksPopup from "../../components/modal/ShareLinksPopup";
import { useBranchCode } from "../../util/hooks/useBranch";

export default function FormList() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [showShareModal, setShowShareModal] = useState<string | null>(null);
  const branchCode = useBranchCode()

  const { loading, data, pagination, page, size } = useSelector(
    (state: RootState) => state.forms,
  );

  const { confirm, modal } = useConfirmToast();

  useEffect(() => {
    dispatch(fetchForms());
  }, [dispatch, page, size]);

  const createdByOptions = Array.from(
    new Set(data?.map((item) => item.createdBy).filter(Boolean)),
  );

  const deleteSection = async (formId: number) => {
    const result = await confirm("Are You sure You want to delete this form");
    if (!result) return;
    await FormApis.deleteForm(formId);
    await dispatch(fetchForms());
    toast.success("Form removed successfully!");
  };

  const columns: Column<FormItem>[] = [
    {
      label: "SL NO.",
      accessor: "formSeqId",
      searchable: false,
    },
    {
      label: "Form Name",
      accessor: "formName",
      searchable: true,
      render: (item) => (
        <div className="text-neutral-400 text-sm font-bold">
          {item.formName}
        </div>
      ),
    },
    {
      label: "Created On",
      accessor: "createdAt",
      searchable: true,
      searchType: "date",
      render: (item) => (
        <span className="text-[13px]">{formatDateTime(item.createdAt)}</span>
      ),
    },
    {
      label: "Created By",
      accessor: "createdBy",
      searchable: true,
      searchType: "select",
      searchOptions: createdByOptions,
      render: (item) => <p className="text-[13px]">{item.createdBy}</p>,
    },
    {
      label: "View Details",
      accessor: "result",
      render: (item) => (
        <div className="w-full">
          <Tag
            size="sm"
            color="warning"
            text="View Details"
            onClick={() =>
              navigate(`/form/create-form/form-response/${item.formSeqId}`)
            }
          />
        </div>
      ),
    },
    {
      label: "Link",
      accessor: "link",
      render: (item) => {
        const formLink = `${window.location.origin}/form/${branchCode}/share/${item.formSeqId}`;

        return (
          <button
            onClick={() => setShowShareModal(formLink)}
            className="flex items-center gap-2"
          >
            <img src={link} alt="link" />
          </button>
        );
      },
    },

    {
      label: "Action",
      accessor: "action",
      render: (item) => (
        <div className="flex gap-6">
          <TooltipWrapper label="Setup">
            <button
              onClick={() =>
                navigate(`/form/create-form/add/${item.formId}`, {
                  state: {
                    formSeqId: item.formSeqId,
                    formName: item.formName,
                  },
                })
              }
            >
              <img src={playIcon} alt="play" />
            </button>
          </TooltipWrapper>

          <TooltipWrapper label="Duplicate">
            <button>
              <img src={copyIcon} alt="duplicate" />
            </button>
          </TooltipWrapper>

          <TooltipWrapper label="Edit">
            <button
              onClick={() =>
                navigate(`/form/create-form/${item.formId}`, {
                  state: {
                    formId: item.formId,
                    formName: item.formName,
                  },
                })
              }
            >
              <img src={edit} alt="edit" className="w-[18px] h-[18px]" />
            </button>
          </TooltipWrapper>

          <TooltipWrapper label="Delete">
            <button onClick={() => deleteSection(item.formId)}>
              <img src={trash} alt="delete" className="w-[18px] h-[18px]" />
            </button>
          </TooltipWrapper>
        </div>
      ),
    },
  ];

  const handleSearchChange = (accessor: string, value: string) => {
    dispatch(setFormFilters({ [accessor]: value }));
    dispatch(fetchForms());
  };

  return (
    <div className="grid gap-5">
      <PaginationBar
        currentPage={page}
        pageSize={size}
        totalItems={pagination?.totalElements ?? 0}
        onPageChange={(newPage) => dispatch(setPage(newPage))}
        onPageSizeChange={(newSize) => dispatch(setSize(newSize))}
      />

      <div className="overflow-x-auto bg-white px-6 py-5 rounded-lg">
        <DataTable
          columns={columns}
          data={data}
          isLoading={loading}
          onSearchChange={handleSearchChange}
        />
      </div>
      {showShareModal && (
        <ShareLinksPopup
          title="Form Link"
          link={showShareModal}
          onClose={() => setShowShareModal(null)}
        />
      )}

      {modal}
    </div>
  );
}
